import { useCallback, useRef, useState } from "react";
import { useTranslate } from "src/i18n";
import { toastHandler } from "src/util";

import type { GalleryItem } from "../components";
import {
  isAxiosErrorWithStatus,
  mapUploadError,
} from "../WatermarkOptionsForm.utils";

interface UploadPayload {
  file: File;
  onProgress?: (percent: number) => void;
}

interface UploadProgressMeta {
  file: File;
  objectUrl: string;
}

interface UseWatermarkUploadParams {
  serverItems: GalleryItem[];
  watermarkImageUrl?: string;
  onSelect: (url: string) => void;
  uploadAction: (payload: UploadPayload) => Promise<unknown>;
  deleteAction: (id: string) => Promise<unknown>;
}

export const useWatermarkUpload = ({
  serverItems,
  watermarkImageUrl,
  onSelect,
  uploadAction,
  deleteAction,
}: UseWatermarkUploadParams) => {
  const { t } = useTranslate();
  const [pendingItems, setPendingItems] = useState<GalleryItem[]>([]);
  const pendingFilesRef = useRef<Map<string, UploadProgressMeta>>(new Map());

  const currentCount =
    serverItems.length + pendingItems.filter((p) => p.isUploading).length;

  const allItems: GalleryItem[] = [...pendingItems, ...serverItems];

  const handleUpload = useCallback(
    async (file: File) => {
      const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const objectUrl = URL.createObjectURL(file);
      pendingFilesRef.current.set(optimisticId, { file, objectUrl });

      setPendingItems((prev) => [
        ...prev,
        {
          id: optimisticId,
          url: objectUrl,
          filename: file.name,
          size: file.size,
          mimeType: file.type,
          uploadedAt: new Date().toISOString(),
          isUploading: true,
          uploadProgress: 0,
          uploadError: null,
        },
      ]);

      try {
        await uploadAction({
          file,
          onProgress: (percent: number) => {
            setPendingItems((prev) =>
              prev.map((item) =>
                item.id === optimisticId
                  ? { ...item, uploadProgress: percent }
                  : item,
              ),
            );
          },
        });
        setPendingItems((prev) =>
          prev.filter((item) => item.id !== optimisticId),
        );
        URL.revokeObjectURL(objectUrl);
        pendingFilesRef.current.delete(optimisticId);
      } catch (error) {
        const errorText = mapUploadError(error);
        setPendingItems((prev) =>
          prev.map((item) =>
            item.id === optimisticId
              ? {
                  ...item,
                  isUploading: false,
                  uploadError: errorText,
                }
              : item,
          ),
        );
        URL.revokeObjectURL(objectUrl);
        pendingFilesRef.current.delete(optimisticId);

        const isExpectedError = isAxiosErrorWithStatus(
          error,
          [400, 413, 415, 409],
        );

        if (!isExpectedError) {
          toastHandler.error({
            name: "uploadWatermark",
            title: t("watermark.error.title", "Ошибка загрузки"),
            content: errorText,
          });
        }
      }
    },
    [uploadAction, t],
  );

  const handleRetry = useCallback(
    (id: string) => {
      const meta = pendingFilesRef.current.get(id);
      if (!meta) return;
      setPendingItems((prev) => prev.filter((item) => item.id !== id));
      handleUpload(meta.file);
    },
    [handleUpload],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (id.startsWith("optimistic-")) {
        setPendingItems((prev) => prev.filter((item) => item.id !== id));
        return;
      }

      try {
        await deleteAction(id);
        const deletedItem = serverItems.find((item) => item.id === id);
        if (deletedItem && watermarkImageUrl === deletedItem.url) {
          onSelect("");
        }
      } catch {
        toastHandler.error({
          name: "deleteWatermark",
          title: t("watermark.error.deleteTitle", "Ошибка удаления"),
          content: t("watermark.error.deleteText", "Не удалось удалить файл"),
        });
      }
    },
    [deleteAction, serverItems, watermarkImageUrl, onSelect, t],
  );

  return {
    allItems,
    currentCount,
    handleUpload,
    handleRetry,
    handleDelete,
  };
};
